import {
  BackButton,
  ErrorAlert,
  MarketTopAssetsCard,
  MostRecentTransactionsCard,
  NewsCard,
  PortfolioCard,
  TradeCard,
  useAnimationLayout,
} from '@/components';
import { NavigationProvider } from '@/components/providers';
import APP_CONFIG from '@/config/app';
import { TokenInterface } from '@/interfaces';
import { URLS } from '@/utils/constants';
import { TRANSACTIONS } from '@/utils/strings';
import { useRouter } from 'next/router';
import { FC, memo, useCallback, useMemo, useRef } from 'react';
const MemoMarketTopAssetsCard = memo(MarketTopAssetsCard);
const MemoNewsCard = memo(NewsCard);
const MemoPortfolioCard = memo(PortfolioCard);
const visualizeElementsAndSpacing = false;
const borderClassToVisualizeElementsAndSpacing = visualizeElementsAndSpacing
  ? 'border-white border'
  : '';
const { MAIN_PADDING } = APP_CONFIG.STYLES;
const spacingConfig = {
  padding: `p${MAIN_PADDING}`,
  gap: `gap${MAIN_PADDING}`,
  gapBetweenSections: `gap${MAIN_PADDING}`,
};
const sizingConfig = {
  sectionWidth: 'w-auto',
  gridColumn3: 'col-span-3',
  gridColumn2: 'col-span-2',
};
const TradeTemplate: FC<{
  initialBuyAsset?: TokenInterface;
  isLoading?: boolean;
}> = ({ initialBuyAsset, isLoading = false }) => {
  const router = useRouter();
  const { setBack } = useAnimationLayout();
  const goBack = useCallback(() => {
    setBack(true);
    if (window.history.length > 1) {
      router.back();
    } else {
      router.push(URLS.home);
    }
  }, [router, setBack]);
  const selectedTokensRef = useRef<TokenInterface[]>([]);
  const setSelectedTokens = useCallback((tokens: TokenInterface[]) => {
    selectedTokensRef.current = tokens;
  }, []);
  const errorTitle = useMemo(
    () => router.query.errorTitle as string,
    [router.query.errorTitle],
  );
  const errorDescription = useMemo(
    () => router.query.errorDescription as string,
    [router.query.errorDescription],
  );
  const hasErrors = useMemo(
    () => !!(errorTitle && errorDescription),
    [errorTitle, errorDescription],
  );
  const gridConfig = useMemo(
    () => ({
      className: `grid grid-cols-5 auto-rows-max ${spacingConfig.gap}`,
    }),
    [],
  );
  return (
    <div className={`flex flex-col ${spacingConfig.gap}`}>
      {hasErrors && (
        <ErrorAlert
          title={errorTitle}
          description={errorDescription}
          toast={true}
        />
      )}
      <div className={sizingConfig.sectionWidth}>
        <BackButton onBackClick={goBack} />
      </div>
      <div {...gridConfig}>
        <div
          className={`${sizingConfig.gridColumn3} flex flex-col ${spacingConfig.gap}`}
        >
          <MemoPortfolioCard showFullList={false} />
          <MostRecentTransactionsCard
            tokens={selectedTokensRef.current}
            cardTitle={
              TRANSACTIONS.CARD_HEADER_WHEN_SHOWING_TRADE_SPECIFIC_TRANSACTIONS
            }
          />
          <MemoMarketTopAssetsCard
            limit={10}
            showSomeDetails
            showAllDetails={false}
            rightAlignLastColumn
          />
        </div>
        <div
          className={`${sizingConfig.gridColumn2} flex flex-col ${spacingConfig.gap}`}
        >
          <NavigationProvider initialScreen="trade-initial">
            <TradeCard
              onTokenSelectionChange={setSelectedTokens}
              initialBuyAsset={initialBuyAsset}
              isLoading={isLoading}
            />
          </NavigationProvider>
          {APP_CONFIG.SHOW_NEWS && <MemoNewsCard articleCountToShow={10} />}
        </div>
      </div>
    </div>
  );
};
export default memo(TradeTemplate);
